import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../../services/api';
import { readItem } from '../../../utils/apiResult';

// FE-15 — ilan panosu (kanban) veri katmanı.
//
// SUNUCU TARAFI SAYFALAMA ŞART: 900 başvurulu bir ilanda "kolonun tamamını çek"
// demek istemciye 900 satır indirmek demekti. Uç her kolonun TOPLAM sayısını ve
// yalnız bir SAYFASINI döner; "Daha fazla" yalnız O KOLONUN bir sonraki sayfasını
// ister (stageId + pageNumber) — diğer kolonlar yeniden çekilmez.
//
// Rotalar (koddan doğrulandı — AtsApplicationController):
//   GET  v1/ats-applications/board?jobPostingId=&stageId=&pageNumber=&pageSize=
//   POST v1/ats-applications/{id}/move-to-stage
const BOARD_PAGE_SIZE = 20;

const useAtsBoardApi = (jobPostingId) => {
  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [board, setBoard] = useState(null);
  const [busyCardId, setBusyCardId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  // Kolon başına yüklenmiş sayfa sayısı — "Daha fazla" bunu artırır.
  const pagesRef = useRef({});

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!jobPostingId) return undefined;
    let alive = true;
    pagesRef.current = {};
    setFetching(true);
    const params = new URLSearchParams({ jobPostingId, pageNumber: 1, pageSize: BOARD_PAGE_SIZE });
    api.get(`/v1/ats-applications/board?${params}`)
      .then((res) => {
        if (!alive) return;
        // api.js OKUMALARDA reject ETMEZ — gerçek sinyal res.data.status.
        const data = readItem(res);
        setLoadFailed(data == null);
        setBoard(data);
      })
      .finally(() => { if (alive) setFetching(false); });
    return () => { alive = false; };
  }, [jobPostingId, refreshTrigger]);

  // Tek kolonun bir sonraki sayfasını EKLER. Diğer kolonlara dokunulmaz.
  const loadMore = useCallback(async (stageId) => {
    const nextPage = (pagesRef.current[stageId] || 1) + 1;
    const params = new URLSearchParams({
      jobPostingId, stageId, pageNumber: nextPage, pageSize: BOARD_PAGE_SIZE,
    });
    const res = await api.get(`/v1/ats-applications/board?${params}`);
    const data = readItem(res);
    const column = data?.columns?.[0];
    if (!column) return false;
    pagesRef.current[stageId] = nextPage;
    setBoard((prev) => (prev ? {
      ...prev,
      columns: prev.columns.map((c) => (c.stageId === stageId
        ? { ...c, totalCount: column.totalCount, cards: [...c.cards, ...(column.cards || [])] }
        : c)),
    } : prev));
    return true;
  }, [jobPostingId]);

  // Sürükle-bırak aşama taşıma TEKİL uca gider — pano kendi kuralını yazmaz,
  // "İşe Alındı için kabul edilmiş teklif" / "referans kontrolü" / terminal
  // kapıları aynen işler. Başarısızlıkta interceptor kendi toast'ını atar ve
  // pano yeniden yüklenerek kart eski yerine döner (iyimser güncelleme YOK:
  // kullanıcı sunucunun reddettiği bir yerleşimi asla doğru sanmamalı).
  // `reasonNote` — BP-26: "Elendi" (terminal disqualified) kolonuna taşımak başvuruyu
  // `disqualified` yapar; backend o geçişte gerekçeyi ZORUNLU kılar
  // (ats.application.disqualifyReasonRequired). Diğer kolonlarda opsiyoneldir.
  const moveCard = useCallback(async (applicationId, toStageId, reasonNote) => {
    setBusyCardId(applicationId);
    try {
      await api.post(`/v1/ats-applications/${applicationId}/move-to-stage`, {
        toStageId, reasonNote: reasonNote || undefined,
      });
      refresh();
      return true;
    } catch {
      refresh();
      return false;
    } finally {
      setBusyCardId(null);
    }
  }, [refresh]);

  return { fetching, loadFailed, board, busyCardId, loadMore, moveCard, refresh };
};

export { BOARD_PAGE_SIZE };
export default useAtsBoardApi;
